import { useState, useEffect } from 'react';
import { db } from '../firebase-config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { NavLink }
    from 'react-router-dom';
import './blog.scss';
import { applySeo, siteUrl } from '../../utils/seo';

const resolveFeaturedImage = (post) => {
    if (post.featured_image_url) {
        return post.featured_image_url;
    }

    if (post.image_url) {
        return post.image_url;
    }

    if (post.image) {
        return `../images/${post.image}`;
    }

    return '';
};

const getPublishedDate = (post) => post.published_at || post.date_created || null;

const Blog = () => {
    const [posts, setPosts] = useState([]); 

    useEffect(() => {
        applySeo({
            title: 'Astrophotography Blog | David Joyce',
            description: 'Read astrophotography articles, project notes, and image writeups from David Joyce.',
            canonicalPath: '/blog/',
            ogImage: `${siteUrl}/logo192.png`,
            structuredData: {
                '@context': 'https://schema.org',
                '@type': 'Blog',
                name: 'AstroDavido Astrophotography Blog',
                description: 'Astrophotography articles and image writeups from David Joyce.',
                url: `${siteUrl}/blog/`
            }
        });

        const getPosts = async () => {
            const postsCollectionRef = collection(db, 'posts');
            const postsQuery = query(postsCollectionRef, where('is_active', '==', 1));
            const data = await getDocs(postsQuery);
            const orderedPosts = data.docs
                .map((doc) => ({ ...doc.data(), id: doc.id }))
                .sort((left, right) => {
                    const leftDate = getPublishedDate(left)?.toDate?.()?.getTime?.() || 0;
                    const rightDate = getPublishedDate(right)?.toDate?.()?.getTime?.() || 0;
                    return rightDate - leftDate;
                });

            setPosts(orderedPosts);
        };

        getPosts();

    }, []);

    return (
        <section className="blog-index">
            <div className="blog-index-shell">
                <header className="blog-index-header">
                    <p className="blog-index-kicker">Astrophotography Journal</p>
                    <h1>Notes from under the night sky</h1>
                    <p className="blog-index-intro">
                        Imaging lessons, field notes, gear thoughts, and the occasional hard-earned reality check from building this hobby one clear night at a time.
                    </p>
                </header>

                {posts.length ? (
                    <div className="blog-card-grid">
                        {posts.map((post) => (
                            <article key={post.id} className="blog-card">
                                <NavLink to={`/blog/${post.url}/`} className="blog-card-image-link">
                                    {resolveFeaturedImage(post) ? (
                                        <img src={resolveFeaturedImage(post)} alt={post.featured_image_alt || post.image_alt || post.title} />
                                    ) : (
                                        <div className="blog-card-image blog-card-image-placeholder">AstroDavido</div>
                                    )}
                                </NavLink>

                                <div className="blog-card-content">
                                    <p className="blog-card-date">{getPublishedDate(post)?.toDate?.()?.toDateString?.() || 'No publish date'}</p>
                                    <h2 className="blog-card-title">
                                        <NavLink to={`/blog/${post.url}/`}>{post.title}</NavLink>
                                    </h2>
                                    <p className="blog-card-summary">{post.summary_markdown || post.summary}</p>
                                    <NavLink to={`/blog/${post.url}/`} className="blog-card-cta">Read article</NavLink>
                                </div>
                            </article>
                        ))}
                    </div>
                ) : (
                    <div className="blog-empty-state">
                        <h2>No published articles yet</h2>
                        <p>The first post will show up here as soon as it is published.</p>
                    </div>
                )}
            </div>
        </section>
    );
  };
    
  export default Blog;
